'use strict'

import React, { useCallback, useMemo } from 'react'
import { ChrofyShowErrors } from '../../../common/Form'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'

const FORM_TITLE = '変換表マッピング設定'
const ERRORS = {
  E10206_07_001: 'ファイル種別を選択してください',
  E10206_07_002: '項目名を選択してください',
  E10206_07_003: '変換表を選択してください',
  E10206_07_004: '入力内容に誤りがあります'
}

/**
 * @typedef {{ MappingFileType?: string; MappingColumnName?: string; ConversionTableKey?: string }} ConversionTable
 *
 * @typedef {{ MappingType: 'ConversionTable'; ConversionTable: ConversionTable }} MappingInfoConversionTable
 */

/**
 * MappingInfo conversion_table → フォーム用（DB→UI）
 *
 * @param {MappingInfoConversionTable | null} initial
 * @param {import('../extra/types').FileTypeInfo[]} fileTypes
 * @returns {{ mappingFileType: string; mappingColumnName: string; conversionTableKey: string }}
 */
export function mappingInfoToFormData(initial, fileTypes) {
  const ct = initial?.ConversionTable
  const firstFile = fileTypes?.[0]
  return {
    mappingFileType: ct?.MappingFileType ?? firstFile?.mappingFileType ?? '',
    mappingColumnName: ct?.MappingColumnName ?? firstFile?.headers?.[0] ?? '',
    conversionTableKey: ct?.ConversionTableKey ?? ''
  }
}

/**
 * フォーム → MappingInfo conversion_table（UI→DB）
 *
 * @param {{ mappingFileType: string; mappingColumnName: string; conversionTableKey: string }} formData
 * @returns {{
 *   MappingType: 'ConversionTable'
 *   ConversionTable: { MappingFileType: string; MappingColumnName: string; ConversionTableKey: string }
 * }}
 */
export function formDataToMappingInfo(formData) {
  return {
    MappingType: 'ConversionTable',
    ConversionTable: {
      MappingFileType: formData.mappingFileType,
      MappingColumnName: formData.mappingColumnName,
      ConversionTableKey: formData.conversionTableKey
    }
  }
}

/**
 * バリデーション（SR-149, SR-150, SR-151）
 *
 * @param {{ mappingFileType: string; mappingColumnName: string; conversionTableKey: string }} formData
 * @returns {Record<string, string[]> | null}
 */
export function validateConversionTableForm(formData) {
  const errors = /** @type {Record<string, string[]>} */ ({})
  if (!formData.mappingFileType?.trim()) {
    errors.mappingFileType = [ERRORS.E10206_07_001]
  }
  if (!formData.mappingColumnName?.trim()) {
    errors.mappingColumnName = [ERRORS.E10206_07_002]
  }
  if (!formData.conversionTableKey?.trim()) {
    errors.conversionTableKey = [ERRORS.E10206_07_003]
  }
  return Object.keys(errors).length > 0 ? errors : null
}

/**
 * 変換表一覧を DisplayOrder 昇順でソート（SR-146）
 *
 * @param {{
 *   conversionTableKey: string
 *   conversionTableName?: string
 *   name?: string
 *   memo?: string
 *   displayOrder?: number
 * }[]} list
 * @returns {{
 *   conversionTableKey: string
 *   conversionTableName?: string
 *   name?: string
 *   memo?: string
 *   displayOrder?: number
 * }[]}
 */
function sortConversionTablesByDisplayOrder(list) {
  if (!Array.isArray(list)) return []
  return [...list].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
}

/**
 * ConversionTableForm 本体（対象項目エリア・変換表エリア・エラー表示）
 *
 * @param {{
 *   fileTypes: import('../extra/types').FileTypeInfo[]
 *   conversionTableList: {
 *     conversionTableKey: string
 *     conversionTableName?: string
 *     name?: string
 *     memo?: string
 *     displayOrder?: number
 *   }[]
 *   formData: { mappingFileType: string; mappingColumnName: string; conversionTableKey: string }
 *   errors: Record<string, string[]>
 *   onChange: (
 *     fd: { mappingFileType: string; mappingColumnName: string; conversionTableKey: string },
 *     setErrors: (e: Record<string, string[]>) => void
 *   ) => void
 * }} props
 */
export const ConversionTableForm = (props) => {
  const { fileTypes = [], conversionTableList = [], formData: rawFormData, errors: rawErrors = {}, onChange } = props

  const formData = {
    mappingFileType: rawFormData?.mappingFileType ?? '',
    mappingColumnName: rawFormData?.mappingColumnName ?? '',
    conversionTableKey: rawFormData?.conversionTableKey ?? ''
  }
  const errors = rawErrors ?? {}

  const sortedConversionTables = useMemo(
    () => sortConversionTablesByDisplayOrder(conversionTableList),
    [conversionTableList]
  )
  const selectedFile = fileTypes.find((f) => f.mappingFileType === formData.mappingFileType)
  const columnOptions = (selectedFile?.headers ?? []).map((h) => ({ label: h, value: h }))
  const conversionTableOptions = sortedConversionTables.map((t) => ({
    label: t.conversionTableName ?? t.name ?? t.conversionTableKey,
    value: t.conversionTableKey
  }))
  const isColumnDisabled = !formData.mappingFileType
  const selectedTable = sortedConversionTables.find((t) => t.conversionTableKey === formData.conversionTableKey)

  const handleFileTypeChange = useCallback(
    (e) => {
      const mappingFileType = e.target.value ?? ''
      onChange({ ...formData, mappingFileType, mappingColumnName: '' }, () => {})
    },
    [formData, onChange]
  )

  const handleColumnChange = useCallback(
    (e) => {
      const mappingColumnName = e.target.value ?? ''
      onChange({ ...formData, mappingColumnName }, () => {})
    },
    [formData, onChange]
  )

  const handleConversionTableChange = useCallback(
    (e) => {
      const conversionTableKey = e.target.value ?? ''
      onChange({ ...formData, conversionTableKey }, () => {})
    },
    [formData, onChange]
  )

  return (
    <div className="text-h7">
      <div className="form-table">
        <table className="w-full">
          <tbody>
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label className="text-h7 font-bold">
                  ファイル種別 <span className="red--text">*</span>
                </label>
              </th>
              <td className="form-table__col-data">
                <ChrofyCustomSelect
                  id="conversion-table-file-type"
                  name="conversion-table-file-type"
                  value={formData.mappingFileType}
                  onChange={handleFileTypeChange}
                  className="w-full"
                  options={(fileTypes || []).map((f) => ({ label: f.mappingFileType, value: f.mappingFileType }))}
                  disabled={false}
                  hasNoSelect={true}
                  searchable
                  isError={Boolean(errors.mappingFileType?.length)}
                  data-testid="conversion-table-file-type"
                />
                <ChrofyShowErrors field="mappingFileType" errors={errors} />
              </td>
            </tr>
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label className="text-h7 font-bold">
                  項目名 <span className="red--text">*</span>
                </label>
              </th>
              <td className="form-table__col-data">
                <ChrofyCustomSelect
                  id="conversion-table-column-name"
                  name="conversion-table-column-name"
                  value={formData.mappingColumnName}
                  onChange={handleColumnChange}
                  className={isColumnDisabled ? 'opacity-60 w-full' : 'w-full'}
                  options={columnOptions}
                  disabled={isColumnDisabled}
                  hasNoSelect={true}
                  searchable
                  isError={Boolean(errors.mappingColumnName?.length)}
                  data-testid="conversion-table-column-name"
                />
                <ChrofyShowErrors field="mappingColumnName" errors={errors} />
              </td>
            </tr>
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label className="text-h7 font-bold">
                  変換表 <span className="red--text">*</span>
                </label>
              </th>
              <td className="form-table__col-data">
                <ChrofyCustomSelect
                  id="conversion-table-select"
                  name="conversion-table-select"
                  value={formData.conversionTableKey}
                  onChange={handleConversionTableChange}
                  className="w-full"
                  options={conversionTableOptions}
                  disabled={false}
                  hasNoSelect={true}
                  isError={Boolean(errors.conversionTableKey?.length)}
                  data-testid="conversion-table-select"
                />
                <ChrofyShowErrors field="conversionTableKey" errors={errors} />
                {selectedTable?.memo && (
                  <p className="mt-2 text-sm grey--text" data-testid="conversion-table-memo">
                    {selectedTable.memo}
                  </p>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
